import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleXmark, faTriangleExclamation, faSpinner } from '@/fontawesome.ts';
import Modal from '@/components/ui/Modal.tsx';
import MultiCreateTable from '@/components/admin/recommendations/MultiCreateTable.tsx';
import RefCheckIndicator from '@/components/admin/symbols/RefCheckIndicator';
import { handleAPIError } from '@/utils/toasts.ts';
import { APIRoutes } from '@/services/api.routes.ts';
import { PAGINATION } from '@/config';
import type { Symbol as SymbolModel, StockExchangeID, StockExchange } from '@/models';
import type { VerifySymbolResponse, VerifySymbolSourceResult } from '@/modelsAPI';
import {
  SYMBOL_PRICE_SOURCE_LABELS,
  formatEODHDInstrument,
  isValidSourceRef,
  normalizeSourceRef,
  refCheckDescription,
  refCheckState,
  symbolSourceTooltip,
  VERIFY_SOURCE_REASON_LABELS,
} from '@/utils/symbol-source';
import type { SymbolPriceSource } from '@/utils/symbol-source';

export interface SymbolEntry {
  rawInput: string;
  parsedTicker: string;
  parsedExchange: string | null;
  existingSymbol: SymbolModel | null;
}

interface CreateMissingSymbolsModalProps {
  isOpen: boolean;
  onClose: () => void;
  symbols: SymbolEntry[];
  onSymbolsReady: (symbolMap: Record<string, SymbolModel>) => void;
}

interface SymbolRow {
  rawInput: string;
  parsedTicker: string;
  existingSymbol: SymbolModel | null;
  exchangeID: StockExchangeID | '';
  name: string;
  sector: string;
  googleSheetsRef: string;
  eodhdRef: string;
  refsTouched: { googleSheets: boolean; eodhd: boolean };
  verifyResult: VerifySymbolResponse | null;
  status: 'existing' | 'verified' | 'unverified' | 'failed';
  verifying: boolean;
}

const UNTOUCHED = { googleSheets: false, eodhd: false };

/** POST /symbols value for a row ref: omitted (= backend default) only while no verify result for that source exists. */
function refForCreate(value: string, touched: boolean, sourceResult: VerifySymbolSourceResult | null): string | null | undefined {
  const ref = normalizeSourceRef(value);
  if (ref !== '') {
    return ref;
  }
  return touched || sourceResult !== null ? null : undefined;
}

function rowStatusTitle(row: SymbolRow, fallback: string): string {
  const result = row.verifyResult;
  if (result === null) {
    return fallback;
  }
  if (result.reason === 'EXISTING_ENTITY') {
    return 'Symbol existiert bereits';
  }
  if (result.reason === 'INVALID_TICKER') {
    return 'Ungültiger Ticker';
  }
  const parts: string[] = [];
  if (result.googleSheets !== null) {
    parts.push(`${SYMBOL_PRICE_SOURCE_LABELS.GSHEETS}: ${VERIFY_SOURCE_REASON_LABELS[result.googleSheets.reason] ?? result.googleSheets.reason}`);
  }
  if (result.eodhd !== null) {
    parts.push(`${SYMBOL_PRICE_SOURCE_LABELS.EODHD}: ${VERIFY_SOURCE_REASON_LABELS[result.eodhd.reason] ?? result.eodhd.reason}`);
  }
  return parts.length > 0 ? `${fallback} – ${parts.join(', ')}` : fallback;
}

export function parseTickerInput(input: string): { ticker: string; exchange: string | null } {
  const trimmed = input.trim().toUpperCase();
  const colonIndex = trimmed.indexOf(':');
  if (colonIndex > 0 && colonIndex < trimmed.length - 1) {
    return { ticker: trimmed.substring(colonIndex + 1), exchange: trimmed.substring(0, colonIndex) };
  }
  return { ticker: trimmed, exchange: null };
}

export default function CreateMissingSymbolsModal({
  isOpen,
  onClose,
  symbols,
  onSymbolsReady
}: CreateMissingSymbolsModalProps) {
  const [rows, setRows] = useState<SymbolRow[]>([]);
  const [exchanges, setExchanges] = useState<StockExchange[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const verifyTimers = useRef<Record<string, NodeJS.Timeout>>({});

  // Fetch exchanges when modal opens
  useEffect(() => {
    if (isOpen) {
      APIRoutes.listStockExchanges({ limit: PAGINATION.FETCH_ALL_LIMIT }).then(response => {
        setExchanges(response.stockExchanges);
      }).catch((err) => {
        handleAPIError(err);
      });
    }
  }, [isOpen]);

  // Initialize rows when symbols change
  useEffect(() => {
    if (!isOpen || symbols.length === 0) return;

    const newRows: SymbolRow[] = symbols.map(entry => {
      if (entry.existingSymbol !== null) {
        return {
          rawInput: entry.rawInput,
          parsedTicker: entry.parsedTicker,
          existingSymbol: entry.existingSymbol,
          exchangeID: entry.existingSymbol.stockExchangeId,
          name: entry.existingSymbol.name ?? '',
          sector: entry.existingSymbol.sector ?? '',
          googleSheetsRef: entry.existingSymbol.googleSheets.ref ?? '',
          eodhdRef: entry.existingSymbol.eodhd.ref ?? '',
          refsTouched: UNTOUCHED,
          verifyResult: null,
          status: 'existing' as const,
          verifying: false
        };
      }

      // New symbol - try to match exchange from parsed input
      let matchedExchangeID: StockExchangeID | '' = '';
      if (entry.parsedExchange !== null && exchanges.length > 0) {
        const match = exchanges.find(e => e.identifier === entry.parsedExchange);
        if (match !== undefined) {
          matchedExchangeID = match.id;
        }
      }

      return {
        rawInput: entry.rawInput,
        parsedTicker: entry.parsedTicker,
        existingSymbol: null,
        exchangeID: matchedExchangeID,
        name: '',
        sector: '',
        googleSheetsRef: '',
        eodhdRef: '',
        refsTouched: UNTOUCHED,
        verifyResult: null,
        status: matchedExchangeID !== '' ? 'unverified' as const : 'unverified' as const,
        verifying: false
      };
    });

    setRows(newRows);
    setError('');

    // Auto-verify rows that have a pre-selected exchange
    for (const row of newRows) {
      if (row.existingSymbol === null && row.exchangeID !== '') {
        verifyRow(row.parsedTicker, row.exchangeID, row.rawInput, newRows);
      }
    }
  }, [isOpen, symbols, exchanges]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      for (const timer of Object.values(verifyTimers.current)) {
        clearTimeout(timer);
      }
    };
  }, []);

  const verifyRow = async (ticker: string, exchangeID: StockExchangeID, rawInput: string, currentRows?: SymbolRow[]) => {
    const updateRows = (updater: (rows: SymbolRow[]) => SymbolRow[]) => {
      if (currentRows !== undefined) {
        currentRows = updater(currentRows);
      }
      setRows(updater);
    };

    updateRows(prev => prev.map(r =>
      r.rawInput === rawInput ? { ...r, verifying: true } : r
    ));

    // A response for an exchange the admin has changed in the meantime is outdated.
    const applies = (r: SymbolRow) => r.rawInput === rawInput && r.exchangeID === exchangeID;

    try {
      const result = await APIRoutes.verifySymbol({ ticker, exchangeId: exchangeID });
      updateRows(prev => prev.map(r =>
        applies(r)
          ? {
              ...r,
              verifying: false,
              verifyResult: result,
              status: result.found ? 'verified' : 'failed',
              googleSheetsRef: !r.refsTouched.googleSheets && result.googleSheets !== null ? (result.googleSheets.ref ?? '') : r.googleSheetsRef,
              eodhdRef: !r.refsTouched.eodhd && result.eodhd !== null ? (result.eodhd.ref ?? '') : r.eodhdRef,
            }
          : r
      ));
    } catch {
      updateRows(prev => prev.map(r =>
        applies(r) ? { ...r, verifying: false, status: 'failed' } : r
      ));
    }
  };

  const handleExchangeChange = (rawInput: string, exchangeID: StockExchangeID | '') => {
    setRows(prev => prev.map(r =>
      r.rawInput === rawInput
        ? {
            ...r,
            exchangeID,
            status: 'unverified',
            verifying: false,
            verifyResult: null,
            googleSheetsRef: r.refsTouched.googleSheets ? r.googleSheetsRef : '',
            eodhdRef: r.refsTouched.eodhd ? r.eodhdRef : '',
          }
        : r
    ));

    // Clear existing timer
    if (verifyTimers.current[rawInput] !== undefined) {
      clearTimeout(verifyTimers.current[rawInput]);
    }

    if (exchangeID !== '') {
      const row = rows.find(r => r.rawInput === rawInput);
      if (row !== undefined && row.existingSymbol === null) {
        verifyTimers.current[rawInput] = setTimeout(() => {
          verifyRow(row.parsedTicker, exchangeID, rawInput);
          delete verifyTimers.current[rawInput];
        }, 500);
      }
    }
  };

  const handleFieldChange = (rawInput: string, field: 'name' | 'sector', value: string) => {
    setRows(prev => prev.map(r =>
      r.rawInput === rawInput ? { ...r, [field]: value } : r
    ));
  };

  const handleRefChange = (rawInput: string, source: SymbolPriceSource, value: string) => {
    setRows(prev => prev.map(r => {
      if (r.rawInput !== rawInput) {
        return r;
      }
      return source === 'GSHEETS'
        ? { ...r, googleSheetsRef: value, refsTouched: { ...r.refsTouched, googleSheets: true } }
        : { ...r, eodhdRef: value, refsTouched: { ...r.refsTouched, eodhd: true } };
    }));
  };

  const allHaveExchange = rows.every(r => r.exchangeID !== '');
  const allRefsValid = rows.every(r =>
    r.existingSymbol !== null || (
      (normalizeSourceRef(r.googleSheetsRef) === '' || isValidSourceRef('GSHEETS', r.googleSheetsRef)) &&
      (normalizeSourceRef(r.eodhdRef) === '' || isValidSourceRef('EODHD', r.eodhdRef))
    )
  );

  const handleCreate = async () => {
    setError('');
    setLoading(true);

    try {
      const symbolMap: Record<string, SymbolModel> = {};

      for (const row of rows) {
        if (row.existingSymbol !== null) {
          // Check if name or sector changed (empty string in UI equals null in backend)
          const effectiveName = row.name !== '' ? row.name : null;
          const effectiveSector = row.sector !== '' ? row.sector : null;
          const nameChanged = effectiveName !== row.existingSymbol.name;
          const sectorChanged = effectiveSector !== row.existingSymbol.sector;

          if (nameChanged || sectorChanged) {
            const updated = await APIRoutes.updateSymbol(row.existingSymbol.id, {
              ticker: row.existingSymbol.ticker,
              name: effectiveName,
              sector: effectiveSector,
              showInTicker: row.existingSymbol.showInTicker,
              stockExchangeId: row.existingSymbol.stockExchangeId,
            });
            symbolMap[row.rawInput] = updated;
          } else {
            symbolMap[row.rawInput] = row.existingSymbol;
          }
        } else {
          // Create new symbol
          if (row.exchangeID === '') {
            setError(`Bitte Börse für "${row.parsedTicker}" auswählen`);
            setLoading(false);
            return;
          }
          const created = await APIRoutes.createSymbol({
            ticker: row.parsedTicker,
            name: row.name !== '' ? row.name : null,
            sector: row.sector !== '' ? row.sector : null,
            showInTicker: false,
            stockExchangeId: row.exchangeID,
            googleSheetsRef: refForCreate(row.googleSheetsRef, row.refsTouched.googleSheets, row.verifyResult?.googleSheets ?? null),
            eodhdRef: refForCreate(row.eodhdRef, row.refsTouched.eodhd, row.verifyResult?.eodhd ?? null),
          });
          symbolMap[row.rawInput] = created;
        }
      }

      // Also map any other rawInputs that resolve to the same parsedTicker
      for (const entry of symbols) {
        if (symbolMap[entry.rawInput] === undefined) {
          // Find by ticker match
          const matchingRow = rows.find(r => r.parsedTicker === entry.parsedTicker);
          if (matchingRow !== undefined && symbolMap[matchingRow.rawInput] !== undefined) {
            symbolMap[entry.rawInput] = symbolMap[matchingRow.rawInput];
          }
        }
      }

      onSymbolsReady(symbolMap);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Fehler beim Erstellen der Symbole';
      setError(errorMessage);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (row: SymbolRow) => {
    if (row.verifying) {
      return <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin text-gray-400" />;
    }

    switch (row.status) {
      case 'existing':
        return <FontAwesomeIcon icon={faCircleCheck} className="w-4 h-4 text-gray-500" title="Symbol existiert bereits" />;
      case 'verified':
        return <FontAwesomeIcon icon={faCircleCheck} className="w-4 h-4 text-green-500" title={rowStatusTitle(row, 'Verifiziert')} />;
      case 'failed':
        return <FontAwesomeIcon icon={faTriangleExclamation} className="w-4 h-4 text-amber-500" title={rowStatusTitle(row, 'Verifizierung fehlgeschlagen')} />;
      case 'unverified':
        if (row.exchangeID === '') {
          return <FontAwesomeIcon icon={faCircleXmark} className="w-4 h-4 text-red-500" title="Börse erforderlich" />;
        }
        return <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin text-gray-400" />;
    }
  };

  const candidatesListId = (row: SymbolRow) => `eodhd-candidates-${rows.findIndex(r => r.rawInput === row.rawInput)}`;

  const renderRefCell = (row: SymbolRow, source: SymbolPriceSource) => {
    if (row.existingSymbol !== null) {
      const stored = source === 'GSHEETS' ? row.existingSymbol.googleSheets : row.existingSymbol.eodhd;
      return (
        <span
          className="text-xs font-mono text-gray-600 dark:text-gray-400"
          title={symbolSourceTooltip(SYMBOL_PRICE_SOURCE_LABELS[source], stored)}
        >
          {stored.ref ?? '—'}
        </span>
      );
    }

    const value = source === 'GSHEETS' ? row.googleSheetsRef : row.eodhdRef;
    const sourceResult = source === 'GSHEETS' ? (row.verifyResult?.googleSheets ?? null) : (row.verifyResult?.eodhd ?? null);
    const state = refCheckState(source, value, sourceResult, row.verifying);
    const candidateCount = source === 'EODHD' ? (row.verifyResult?.eodhd?.candidates.length ?? 0) : 0;
    const hasCandidates = candidateCount > 0;

    return (
      <div className="flex items-center gap-1.5">
        <input
          type="text"
          value={value}
          onChange={(e) => handleRefChange(row.rawInput, source, e.target.value.toUpperCase())}
          disabled={loading}
          list={hasCandidates ? candidatesListId(row) : undefined}
          className="w-full md:w-32 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs font-mono"
          placeholder={source === 'GSHEETS' ? 'BÖRSE:TICKER' : 'TICKER.BÖRSE'}
          autoComplete="off"
          spellCheck={false}
        />
        <RefCheckIndicator
          source={source}
          state={state}
          text={refCheckDescription(source, state, sourceResult, false) + (hasCandidates ? ` (${candidateCount} EODHD-Treffer als Vorschlag im Eingabefeld)` : '')}
          compact
        />
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Fehlende Symbole"
      icon={<FontAwesomeIcon icon={faCircleCheck} className="w-5 h-5 text-blue-600" />}
      maxWidth="6xl"
      closeDisabled={loading}
    >
      <div className="p-4 sm:p-6 space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Die folgenden Symbole existieren nicht in der Datenbank und müssen erstellt werden.
        </p>

        <MultiCreateTable
          rows={rows}
          keyExtractor={(row) => row.rawInput}
          fields={[
            {
              key: 'ticker',
              header: 'Ticker',
              primary: true,
              render: (row) => (
                <span className="text-sm font-mono text-gray-900 dark:text-white">
                  {row.parsedTicker}
                </span>
              )
            },
            {
              key: 'exchange',
              header: 'Börse *',
              render: (row) => (
                <select
                  value={row.exchangeID}
                  onChange={(e) => handleExchangeChange(row.rawInput, e.target.value as StockExchangeID)}
                  disabled={row.existingSymbol !== null || loading}
                  className={`w-full md:w-40 px-2 py-1 border rounded text-sm cursor-pointer ${
                    row.existingSymbol !== null
                      ? 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-500 cursor-not-allowed'
                      : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <option value="">-- Börse wählen --</option>
                  {exchanges.map(ex => (
                    <option key={ex.id} value={ex.id}>{ex.identifier} - {ex.name}</option>
                  ))}
                </select>
              )
            },
            {
              key: 'name',
              header: 'Name',
              render: (row) => (
                <input
                  type="text"
                  value={row.name}
                  onChange={(e) => handleFieldChange(row.rawInput, 'name', e.target.value)}
                  disabled={loading}
                  className="w-full md:w-32 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  placeholder="Optional"
                />
              )
            },
            {
              key: 'sector',
              header: 'Branche',
              render: (row) => (
                <input
                  type="text"
                  value={row.sector}
                  onChange={(e) => handleFieldChange(row.rawInput, 'sector', e.target.value)}
                  disabled={loading}
                  className="w-full md:w-32 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  placeholder="Optional"
                />
              )
            },
            {
              key: 'googleSheetsRef',
              header: 'Google Sheets',
              mobileHalf: true,
              render: (row) => renderRefCell(row, 'GSHEETS')
            },
            {
              key: 'eodhdRef',
              header: 'EODHD',
              mobileHalf: true,
              render: (row) => renderRefCell(row, 'EODHD')
            },
            {
              key: 'status',
              header: 'Status',
              align: 'center',
              mobileTrailing: true,
              render: (row) => getStatusIcon(row)
            }
          ]}
        />

        {/* Rendered once outside the table: MultiCreateTable renders every row twice (desktop + mobile). */}
        {rows.map(row => {
          const candidates = row.verifyResult?.eodhd?.candidates ?? [];
          if (row.existingSymbol !== null || candidates.length === 0) {
            return null;
          }
          return (
            <datalist key={row.rawInput} id={candidatesListId(row)}>
              {candidates.map(candidate => (
                <option key={candidate.ref} value={candidate.ref}>{formatEODHDInstrument(candidate)}</option>
              ))}
            </datalist>
          );
        })}

        {error !== '' && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={loading || !allHaveExchange || !allRefsValid}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                Erstelle Symbole...
              </>
            ) : (
              'Symbole erstellen & fortfahren'
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
